import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { Observable, throwError, timer } from 'rxjs';
import { catchError, retry } from 'rxjs/operators';

const MAX_RETRIES = 4;
export const ApiErrorInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> => {
  return next(req).pipe(
    // --- 1. Retry Logic for Transient Network/Gateway Errors (e.g., 502) ---
    retry({
      count: MAX_RETRIES,
      delay: (error: HttpErrorResponse, retryCount: number) => {
        // Only retry for transient errors: 502 Bad Gateway, 503 Service Unavailable, 504 Gateway Timeout
        if (error.status === 502 || error.status === 503 || error.status === 504) {
          // Calculate exponential delay (Base: 5 seconds, doubling each time)
          // 1st retry: 3s, 2nd retry: 6s, 3rd retry: 12s, 4th retry: 24s
          const delayTime = 3000 * Math.pow(2, retryCount - 1);

          console.warn(
            `[AuthInterceptor] Retrying request (Attempt ${retryCount}/${MAX_RETRIES}) after ${
              delayTime / 1000
            }s due to HTTP ${error.status} for URL: ${req.url}`,
          );

          // Return a timer Observable that emits after the calculated delay
          return timer(delayTime);
        }

        // If it's not a transient error, do not retry and re-throw the error immediately
        return throwError(() => error);
      },
    }),

    // --- 2. Post-Retry Error Handling (Authentication/Authorization) ---
    catchError((error: HttpErrorResponse) => {
      // This catchError block is reached if:
      // a) The error was not a transient 5xx error (i.e., 401, 403, 400).
      // b) The error was a transient 5xx error, but retries failed (MAX_RETRIES reached).

      if (error.status === 403) {
        // Forbidden: User is authenticated but lacks necessary permissions.
        // We throw the error up the chain to be handled by the component/store.
        return throwError(() => error);
      }

      if (error.status === 401) {
        // Unauthorized: Access Token is missing or invalid.
        // In the current application design, the `AuthStore.loadCurrentUser`
        // handles this by detecting the 401 error and triggering a silent refresh.
        // We must re-throw the error here so the store/service can catch it.
      }

      // For all other errors (400, final failed 5xx, etc.), simply re-throw.
      return throwError(() => error);
    }),
  );
};
